import { useEffect, useState, useRef } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import TextInput from "../../../components/textInput/TextInput";
import SearchBar from "../../../components/searchBar/SearchBar";
import {
  getDataWithProductAttributes,
  getCustomerIdentity,
  getProductNameByForm,
  addServiceRelocation,
} from "../../../store/actions/fault.action";
import { useLocation } from "react-router-dom";
import MainLoader from "../../../components/loading/MainLoader";
import AlertModal from "../../../components/alertModal/alertModal";
import { useDispatch } from "react-redux";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IServiceRelocationCreateRequest } from "../../../interfaces/fault";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { BASE_URL } from "../../../const/URL";
import ServiceRelocationSuccessMessage from "./ServiceRelocationSuccess/ServiceRelocation";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const center = {
  lat: 6.9271,
  lng: 79.8612,
};

const libraries = ["places"] as const;

export enum EInputMode {
  primary,
  light,
}

interface IRelocationStatusResponse {
  dataBundle: {
    portalDisplayName: string;
    reLocation: "Yes" | "No" | null;
  };
  isSuccess: boolean;
  errorMessage: string | null;
  successMessage: string | null;
}

const SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH = 20;
const SERVICE_LOCATION_IDENTIFIER_PLACEHOLDER =
  "You may provide a location identifier for ease of reference (limit this to maximum 20 characters)";

export interface IServiceRelocationFormInputs {
  serviceId: string;
  newServiceAddress: string;
  serviceLocationIdentifier?: string;
  serviceRequiredDate: Date | null;
  newContactName: string;
  newContactPhone: string;
}

interface IErrorResponse {
  result: boolean;
  message: string;
  displayMessage: string;
  statusCode: number;
  isSuccess: boolean;
}

const ServiceRelocation = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<IServiceRelocationFormInputs>();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [serviceDisplay, setServiceDisplay] = useState("");
  const [serviceAddress, setServiceAddress] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState("");
  const [ServiceID, setServiceIDValue] = useState("");
  const dispatch: any = useDispatch();
  const [success, setSuccess] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [serviceNewAddress, setNewAddress] = useState("");
  const [relocationStatus, setRelocationStatus] = useState<
    "Yes" | "No" | null | undefined
  >();

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyA5UX3U64eX8cLCjVChn92x-6rIYBa_eNE",
    libraries: libraries as any,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const serviceParamID = (state as any)?.customerAccountNumber;

  // Handle place selection from Autocomplete
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSelectedLocation({ lat, lng });
      }
    }
  };

  // Handle map click to select location
  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setSelectedLocation({ lat, lng });
    }
  };

  useEffect(() => {
    if (serviceParamID) {
      handleSearch(serviceParamID);
    }
  }, [serviceParamID]);

  // Set the Service ID value in the form when it changes
  useEffect(() => {
    if (ServiceID) {
      setValue("serviceId", ServiceID);
    }
  }, [ServiceID, setValue]);

  // Handle search for service ID
  const handleSearch = (value: string) => {
    if (!value) return;

    setIsVerified(false);
    setServiceIDValue("");
    setServiceAddress("");
    setContactName("");
    setContactPhone("");
    setNewAddress("");
    setServiceDisplay("");
    setRelocationStatus(undefined);
    setError("");
    setSuccess("");

    reset({
      serviceId: "",
      newServiceAddress: "",
      serviceLocationIdentifier: "",
      serviceRequiredDate: null,
      newContactName: "",
      newContactPhone: "",
    });

    setIsLoading(true);
    getCustomerIdentity(value)
      .then((response) => {
        setIsVerified(true);
        setServiceIDValue(value);

        getDataWithProductAttributes(value)
          .then((response) => {
            setServiceAddress(
              response?.serviceAccountAddress +
                " " +
                response?.serviceAccountAddress2 +
                " " +
                response?.serviceAccountAddress3 +
                " " +
                response?.serviceAccountCity
            );
            setContactName(response?.technicalContactName);
            setContactPhone(response?.technicalContactMobileNo);
            // Check relocation status
            checkRelocationStatus(response?.productName)
              .then((relocationResponse) => {
                if (relocationResponse.dataBundle.reLocation === "Yes") {
                  setRelocationStatus(relocationResponse.dataBundle.reLocation);
                  setServiceDisplay(
                    relocationResponse.dataBundle.portalDisplayName
                  );

                  setIsLoading(false);
                  return;
                }
              })
              .catch((error) => {
                setIsLoading(false);
                setShowAlert(true);
                setRelocationStatus("No");
                setError(error.message || "Service relocation check failed");
              });
          })
          .catch((error) => {
            setIsLoading(false);
          });
      })
      .catch((error: IErrorResponse) => {
        setIsLoading(false);
        setIsVerified(false);
        setShowAlert(true);
        setError(error?.message || "An unexpected error occurred");
      });
  };

  useEffect(() => {
    if (serviceNewAddress) {
      setValue("newServiceAddress", serviceNewAddress);
    }
  }, [serviceNewAddress, setValue]);

  useEffect(() => {
    if (contactName) {
      setValue("newContactName", contactName);
    }
  }, [contactName, setValue]);

  useEffect(() => {
    if (contactPhone) {
      setValue("newContactPhone", contactPhone.match(/\d{1,10}/)?.[0] || "");
    }
  }, [contactPhone, setValue]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("enterprise_portal_access_Token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const checkRelocationStatus = async (prodName: string) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/service-modification/re-location`,
        {
          method: "POST",
          headers: getAuthHeader(),
          body: JSON.stringify({ prodName }),
        }
      );
      const data: IRelocationStatusResponse = await response.json();

      if (!response.ok) {
        throw new Error(data?.errorMessage || "API request failed");
      }

      if (!data.isSuccess) {
        throw new Error(data.errorMessage || "API request failed");
      }

      return data;
    } catch (error: any) {
      console.error("Error fetching relocation status:", error);
      throw new Error(error.message || "Failed to fetch relocation status");
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: IServiceRelocationFormInputs) => {
    setIsLoading(true);
    try {
      const mappedData: IServiceRelocationCreateRequest = {
        description: `Service Relocation Request for ${serviceDisplay} (${
          data.serviceId
        })| Address : ${
          data.newServiceAddress
        } | Required Date : ${data.serviceRequiredDate?.toLocaleDateString()}| requested by : ${
          data.newContactName
        } | Cordinates :(${selectedLocation?.lat},${selectedLocation?.lng})`,
        sltAltContactNumber: data.newContactPhone,
        serviceId: data.serviceId,
        customerReferenceNumber: getCustomerReferenceNumber(),
        serviceAddress: data.newServiceAddress,
        serviceLocationIdentifier: data.serviceLocationIdentifier?.trim() || "",
      };

      await dispatch(addServiceRelocation(mappedData, setSuccess));
      setSelectedLocation(null);
      setShowAlert(true);
    } catch (error: any) {
      setError(error?.message || "Service relocation failed");
      setShowAlert(true);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      reset({
        serviceId: "",
        newServiceAddress: "",
        serviceRequiredDate: null,
        newContactName: "",
        newContactPhone: "",
      });
      setServiceAddress("");
      setServiceDisplay("");
      setIsVerified(false);
    }
  }, [success, reset]);

  return (
    <div className="page-container">
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/serviceRequest/serviceRelocation",
            title: "Service Relocation",
          },
        ]}
      />

      {/* Error Alert Modal */}
      {error && (
        <AlertModal
          show={showAlert}
          variant="error"
          buttons={[
            {
              text: "Ok",
              onClick: () => setShowAlert(false),
              variant: "success",
            },
          ]}
        >
          <label>{error}</label>
        </AlertModal>
      )}

      {/* Success Alert Modal */}
      {/* {success && (
        <AlertModal
          show={showAlert}
          variant="success"
          buttons={[
            {
              text: "Ok",
              onClick: () => setShowAlert(false),
              variant: "success",
            },
          ]}
        >
          <label>
            You have successfully submitted the service relocation and Your
            Reference Number is: {success}
          </label>
        </AlertModal>
      )} */}

      {/* Main Loader */}
      <MainLoader show={isLoading} />

      {success ? (
        <ServiceRelocationSuccessMessage
          handleClose={() => setSuccess("")}
          referenceNo={success}
          faultType={"service"}
        />
      ) : (
        <>
          {/* Search Bar */}
          <Row className="justify-content-center mt-2 mb-5">
            <Col className="col-md-5">
              <SearchBar
                onSearch={handleSearch}
                defaultValue={serviceParamID}
                placeholder="Service Number / ID"
              />
            </Col>
          </Row>

          {/* Relocation Form */}
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="poppins-semibold"
            style={{
              opacity: isVerified && relocationStatus === "Yes" ? 1 : 0.6,
              pointerEvents:
                isVerified && relocationStatus === "Yes" ? "auto" : "none",
              filter:
                isVerified && relocationStatus === "Yes" ? "none" : "blur(2px)",
              backgroundColor:
                isVerified && relocationStatus === "Yes" ? "white" : "#f0f0f0",
              transition: "all 0.3s ease-in-out",
              padding: "15px",
              borderRadius: "8px",
            }}
          >
            {/* Existing Information Section */}
            <h5 className="mb-4">Existing Information</h5>
            <Row className="row-cols-1 row-cols-md-2 gy-3 mb-4">
              <Col>
                <TextInput
                  disabled
                  label="Service Name"
                  mode={EInputMode.light}
                  color="gray"
                  value={serviceDisplay}
                />
              </Col>
              <Col>
                <TextInput
                  disabled
                  label="Service Address"
                  mode={EInputMode.light}
                  color="gray"
                  value={serviceAddress}
                />
              </Col>
            </Row>

            {/* Relocation Information Section */}
            <h5 className="mb-4">Relocation Information</h5>
            <Row className="row-cols-1 row-cols-md-2 gy-3">
              <Col>
                <TextInput
                  label="New Service Address"
                  required
                  mode={EInputMode.light}
                  value={watch("newServiceAddress")}
                  color="gray"
                  useFormRegister={{
                    ...register("newServiceAddress", { required: true }),
                  }}
                  errors={[
                    {
                      isShow: errors.newServiceAddress?.type === "required",
                      message: "New Service Address is required",
                    },
                  ]}
                />
              </Col>
              <Col>
                <TextInput
                  label="Service Location Identifier"
                  mode={EInputMode.light}
                  color="gray"
                  placeholder={SERVICE_LOCATION_IDENTIFIER_PLACEHOLDER}
                  maxLength={SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH}
                  value={watch("serviceLocationIdentifier")}
                  useFormRegister={register("serviceLocationIdentifier")}
                />
              </Col>
              <Col>
                <div className="datepicker-wrapper">
                  <label style={{ color: "#2c62ae" }}>
                    Service Required Date{" "}
                  </label>
                  <span className="text-danger">*</span>
                  <DatePicker
                    selected={watch("serviceRequiredDate")}
                    onChange={(date: Date) =>
                      setValue("serviceRequiredDate", date)
                    }
                    dateFormat="dd/MM/yyyy"
                    className="form-inputV2"
                    placeholderText="Select date"
                    minDate={
                      new Date(new Date().setDate(new Date().getDate() + 5))
                    }
                    required
                  />
                  {errors.serviceRequiredDate && (
                    <span className="text-danger">
                      Service Required Date is required
                    </span>
                  )}
                </div>
              </Col>
              <Col>
                <TextInput
                  label="New Contact Name"
                  required
                  mode={EInputMode.light}
                  value={watch("newContactName")}
                  color="gray"
                  useFormRegister={{
                    ...register("newContactName", { required: true }),
                  }}
                  errors={[
                    {
                      isShow: errors.newContactName?.type === "required",
                      message: "Contact Name is required",
                    },
                  ]}
                />
              </Col>
              <Col>
                <TextInput
                  label="New Contact Number"
                  required
                  mode={EInputMode.light}
                  color="gray"
                  value={watch("newContactPhone")}
                  useFormRegister={{
                    ...register("newContactPhone", {
                      required: true,
                      pattern: /^0[7][0-9]{8}$/,
                    }),
                  }}
                  errors={[
                    {
                      isShow: errors.newContactPhone?.type === "required",
                      message: "Contact Phone is required",
                    },
                    {
                      isShow: errors.newContactPhone?.type === "pattern",
                      message: "Invalid phone number format (07XXXXXXXX)",
                    },
                  ]}
                />
              </Col>
            </Row>

            {/* Google Map for Location Selection */}
            <Row className="mt-4">
              <Col>
                <h5 className="mb-3">Select New Location</h5>
                {isLoaded ? (
                  <>
                    {/* Autocomplete Search Bar */}
                    <Autocomplete
                      onLoad={(autocomplete) =>
                        (autocompleteRef.current = autocomplete)
                      }
                      onPlaceChanged={onPlaceChanged}
                    >
                      <input
                        type="text"
                        placeholder="Enter a new address"
                        style={{
                          width: "100%",
                          padding: "10px",
                          marginBottom: "10px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </Autocomplete>

                    {/* Google Map */}
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      zoom={10}
                      center={selectedLocation || center}
                      onClick={handleMapClick}
                    >
                      {selectedLocation && (
                        <Marker position={selectedLocation} />
                      )}
                    </GoogleMap>
                  </>
                ) : (
                  <div>Loading Google Maps...</div>
                )}
              </Col>
            </Row>

            {/* Submit Button */}
            <Row className="mt-5 justify-content-center">
              <Col className="col-md-3">
                <Button
                  type="submit"
                  className="w-100"
                  style={{
                    backgroundColor: "#51B848",
                    borderColor: "#51B848",
                    fontSize: "16px",
                  }}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </form>
        </>
      )}
    </div>
  );
};

export default ServiceRelocation;
